import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  Info,
  LoaderCircle,
  Send,
} from "lucide-react";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import { PageError } from "../components/PageError";
import type { UploadBatchDetail, UploadBatchItem } from "../types/compliance";
import {
  Badge,
  Button,
  DatePicker,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui";

type PageState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | { status: "ready"; data: UploadBatchDetail };

export function BatchReviewPage() {
  const { cdassettype = "", cdbatch = "" } = useParams();
  const navigate = useNavigate();
  const initializedBatch = React.useRef<number | null>(null);
  const [state, setState] = React.useState<PageState>({ status: "loading" });
  const [validity, setValidity] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [busyItem, setBusyItem] = React.useState<number | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  const [actionError, setActionError] = React.useState("");

  const load = React.useCallback(
    async (showLoading = true) => {
      if (showLoading) setState({ status: "loading" });
      try {
        const response = await portalApi.get<ApiEnvelope<UploadBatchDetail>>(
          `/portal/requirements/batches/${Number(cdbatch)}`
        );
        const data = response.data.data;
        setState({ status: "ready", data });
        if (initializedBatch.current !== data.batch.cdbatch) {
          initializedBatch.current = data.batch.cdbatch;
          setValidity(data.batch.dtvalidity?.slice(0, 10) ?? "");
          setComment(data.batch.txcomment ?? "");
        }
      } catch (requestError: any) {
        if (requestError.response?.status === 403) {
          setState({ status: "forbidden" });
        } else {
          setState({
            status: "error",
            message: getApiErrorMessage(
              requestError,
              "Não foi possível carregar o lote."
            ),
          });
        }
      }
    },
    [cdbatch]
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  async function resolve(
    item: UploadBatchItem,
    idmatch: "manual" | "ignored",
    cdsubject?: number
  ) {
    setBusyItem(item.cdbatchitem);
    setActionError("");
    try {
      await portalApi.patch(
        `/portal/requirements/batch-items/${item.cdbatchitem}`,
        {
          idmatch,
          cdsubject: idmatch === "manual" ? cdsubject : null,
        }
      );
      await load(false);
    } catch (requestError) {
      setActionError(
        getApiErrorMessage(
          requestError,
          "Não foi possível atualizar o vínculo."
        )
      );
    } finally {
      setBusyItem(null);
    }
  }

  async function useDuplicated(item: UploadBatchItem, data: UploadBatchDetail) {
    if (!item.cdsubject) return;
    setBusyItem(item.cdbatchitem);
    setActionError("");
    try {
      const siblings = data.items.filter(
        (candidate) =>
          candidate.idmatch === "duplicated" &&
          candidate.cdsubject === item.cdsubject &&
          candidate.cdbatchitem !== item.cdbatchitem
      );
      for (const sibling of siblings) {
        await portalApi.patch(
          `/portal/requirements/batch-items/${sibling.cdbatchitem}`,
          { idmatch: "ignored", cdsubject: null }
        );
      }
      await portalApi.patch(
        `/portal/requirements/batch-items/${item.cdbatchitem}`,
        { idmatch: "manual", cdsubject: item.cdsubject }
      );
      await load(false);
    } catch (requestError) {
      setActionError(
        getApiErrorMessage(
          requestError,
          "Não foi possível resolver a duplicidade."
        )
      );
    } finally {
      setBusyItem(null);
    }
  }

  async function confirm(data: UploadBatchDetail) {
    if (data.batch.fgvalidcontrol === 1 && !validity) {
      setActionError("Informe a validade da remessa.");
      return;
    }
    if (data.summary.unresolved > 0) {
      setActionError("Resolva ou ignore todos os itens pendentes.");
      return;
    }
    setConfirming(true);
    setActionError("");
    try {
      await portalApi.patch(
        `/portal/requirements/batches/${data.batch.cdbatch}`,
        {
          ...(validity ? { dtvalidity: validity } : { dtvalidity: null }),
          txcomment: comment.trim() || null,
        }
      );
      const response = await portalApi.post<ApiEnvelope<unknown[]>>(
        `/portal/requirements/batches/${data.batch.cdbatch}/confirm`
      );
      navigate(`/assets/${cdassettype}?sent=${response.data.data.length}`, {
        replace: true,
      });
    } catch (requestError) {
      setActionError(
        getApiErrorMessage(requestError, "Não foi possível confirmar o lote.")
      );
    } finally {
      setConfirming(false);
    }
  }

  if (state.status === "loading") return <ReviewSkeleton />;
  if (state.status === "forbidden") {
    return (
      <PageError
        title="Acesso não permitido"
        description="Seu perfil não pode conferir este lote."
      />
    );
  }
  if (state.status === "error") {
    return (
      <PageError
        title="Não foi possível carregar"
        description={state.message}
        retry={() => void load()}
      />
    );
  }

  const { data } = state;
  if (data.batch.fgstatus !== 1) {
    return (
      <section className="rounded-2xl border border-teal-200 bg-teal-50 p-8 text-center">
        <CheckCircle2 className="mx-auto text-teal-700" size={30} />
        <h1 className="mt-3 text-lg font-semibold text-teal-950">
          Lote já confirmado
        </h1>
        <p className="mt-1 text-sm text-teal-800">
          Os documentos deste lote já foram enviados.
        </p>
        <Button asChild className="mt-5" variant="outline">
          <Link to={`/assets/${cdassettype}`}>Voltar para ativos</Link>
        </Button>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-start gap-3">
          <Button
            asChild
            className="mt-0.5 bg-white"
            variant="outline"
            size="icon"
          >
            <Link
              to={`/assets/${cdassettype}`}
              aria-label="Voltar para ativos"
            >
              <ArrowLeft size={18} />
            </Link>
          </Button>
          <div>
            <p className="text-sm font-medium text-teal-700">
              {data.batch.nmassettype}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
              Envio em massa · {data.batch.sgrequirement}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {data.batch.nmrequirement}
            </p>
          </div>
        </div>
        <ol className="mt-5 flex flex-wrap gap-4 text-xs">
          <Step number={1} label="Documento" done />
          <Step number={2} label="Arquivos" done />
          <Step number={3} label="Conferência" active />
          <Step number={4} label="Enviar" />
        </ol>
      </header>

      <section className="grid grid-cols-3 gap-3">
        <SummaryCard
          label="Vinculados"
          value={data.summary.linked}
          tone="bg-teal-50 text-teal-800"
        />
        <SummaryCard
          label="Sem vínculo"
          value={data.summary.unresolved}
          tone="bg-amber-50 text-amber-800"
        />
        <SummaryCard
          label="Ignorados"
          value={data.summary.ignored}
          tone="bg-slate-100 text-slate-700"
        />
      </section>

      <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <Info className="mt-0.5 shrink-0" size={18} />
        <p>
          O vínculo procura primeiro o código do ativo e depois seu documento
          normalizado no nome do arquivo. Nada é gravado como documento enviado
          antes da confirmação.
        </p>
      </div>

      <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2">
        {data.batch.fgvalidcontrol === 1 && (
          <div>
            <Label htmlFor="batch-validity">
              Validade para todos <span className="text-red-600">*</span>
            </Label>
            <DatePicker
              id="batch-validity"
              className="mt-1.5 bg-white"
              value={validity}
              onValueChange={setValidity}
              disabled={confirming}
              required
              placeholder="Selecione a validade"
            />
          </div>
        )}
        <div>
          <Label htmlFor="batch-comment">
            Comentário da remessa{" "}
            <span className="font-normal text-slate-500">(opcional)</span>
          </Label>
          <Input
            id="batch-comment"
            type="text"
            maxLength={1000}
            className="mt-1.5 bg-white"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={confirming}
            placeholder="Informação que acompanhará os documentos"
          />
        </div>
      </section>

      {actionError && (
        <div className="portal-message portal-message-error" role="alert">
          <AlertCircle size={18} />
          <p>{actionError}</p>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {data.items.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto text-slate-400" size={27} />
            <h2 className="mt-3 font-semibold text-slate-900">
              Lote sem arquivos
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Volte e selecione os arquivos antes de confirmar.
            </p>
          </div>
        ) : (
          data.items.map((item) => (
            <BatchItemRow
              key={item.cdbatchitem}
              item={item}
              data={data}
              busy={busyItem === item.cdbatchitem}
              onResolve={(cdsubject) => void resolve(item, "manual", cdsubject)}
              onIgnore={() => void resolve(item, "ignored")}
              onUseDuplicate={() => void useDuplicated(item, data)}
            />
          ))
        )}
      </section>

      <footer className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
        <p className="text-sm text-slate-600">
          {data.summary.total} arquivos · {data.summary.linked} prontos ·{" "}
          {data.summary.unresolved} aguardando resolução ·{" "}
          {data.summary.ignored} ignorados
        </p>
        <Button
          type="button"
          disabled={
            confirming ||
            data.summary.unresolved > 0 ||
            data.summary.linked === 0 ||
            (data.batch.fgvalidcontrol === 1 && !validity)
          }
          onClick={() => void confirm(data)}
        >
          {confirming ? (
            <LoaderCircle className="animate-spin" size={17} />
          ) : (
            <Send size={17} />
          )}
          Enviar {data.summary.linked} arquivos
        </Button>
      </footer>
    </div>
  );
}

function BatchItemRow({
  item,
  data,
  busy,
  onResolve,
  onIgnore,
  onUseDuplicate,
}: {
  item: UploadBatchItem;
  data: UploadBatchDetail;
  busy: boolean;
  onResolve(cdsubject: number): void;
  onIgnore(): void;
  onUseDuplicate(): void;
}) {
  const problem = ["unmatched", "blocked", "duplicated"].includes(item.idmatch);
  const Icon =
    item.idmatch === "duplicated"
      ? Copy
      : problem
      ? AlertTriangle
      : item.idmatch === "ignored"
      ? Check
      : FileText;
  return (
    <article
      className={`flex flex-wrap items-center gap-3 border-b border-slate-200 p-4 last:border-b-0 ${
        ["unmatched", "blocked"].includes(item.idmatch)
          ? "bg-amber-50"
          : item.idmatch === "duplicated"
          ? "bg-slate-50"
          : ""
      }`}
    >
      <Icon
        className={problem ? "text-amber-700" : "text-slate-500"}
        size={19}
      />
      <div className="min-w-52 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">
          {item.nmfilename}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{matchReason(item)}</p>
      </div>

      {busy ? (
        <LoaderCircle className="animate-spin text-teal-700" size={18} />
      ) : ["unmatched", "blocked"].includes(item.idmatch) ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            onValueChange={(selected) => {
              const value = Number(selected);
              if (value) onResolve(value);
            }}
          >
            <SelectTrigger
              className="min-w-56 bg-white"
              aria-label={`Vincular ${item.nmfilename} a um ativo`}
            >
              <SelectValue placeholder="Escolher ativo…" />
            </SelectTrigger>
            <SelectContent>
              {data.assets.map((asset) => (
                <SelectItem
                  value={String(asset.cdasset)}
                  key={asset.cdasset}
                >
                  {asset.idasset} · {asset.nmasset}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={onIgnore}>
            Ignorar
          </Button>
        </div>
      ) : item.idmatch === "duplicated" ? (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onIgnore}>
            Ignorar
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-teal-600 text-teal-700"
            onClick={onUseDuplicate}
          >
            Usar este
          </Button>
        </div>
      ) : (
        <>
          <div className="min-w-48 text-sm text-slate-700">
            {item.idmatch === "ignored" ? (
              "Não será enviado"
            ) : (
              <>
                {item.nmasset || "Ativo"}{" "}
                <span className="text-slate-500">{item.idasset}</span>
              </>
            )}
          </div>
          <Badge
            variant="outline"
            className={`rounded-full ${
              item.idmatch === "ignored"
                ? "border-slate-200 bg-slate-100 text-slate-500"
                : "border-teal-200 bg-teal-50 text-teal-700"
            }`}
          >
            {item.idmatch === "ignored" ? "Ignorado" : "Vinculado"}
          </Badge>
        </>
      )}
    </article>
  );
}

function matchReason(item: UploadBatchItem) {
  if (item.idmatch === "code") {
    return `Código ${item.nmmatchtoken} encontrado no nome`;
  }
  if (item.idmatch === "document") {
    return `Documento ${item.nmmatchtoken} encontrado no nome`;
  }
  if (item.idmatch === "manual") return "Vínculo escolhido manualmente";
  if (item.idmatch === "blocked") {
    return "O ativo possui documento aprovado vigente que não pode ser substituído";
  }
  if (item.idmatch === "duplicated") {
    return "Outro arquivo desta remessa aponta para o mesmo ativo";
  }
  if (item.idmatch === "ignored") return "Arquivo marcado para não enviar";
  return "Nenhum código ou documento cadastrado foi encontrado no nome";
}

function Step({
  number,
  label,
  done,
  active,
}: {
  number: number;
  label: string;
  done?: boolean;
  active?: boolean;
}) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`flex size-6 items-center justify-center rounded-full ${
          done
            ? "bg-teal-50 text-teal-700"
            : active
            ? "bg-teal-700 text-white"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {done ? <Check size={14} /> : number}
      </span>
      <span
        className={active ? "font-medium text-slate-900" : "text-slate-500"}
      >
        {label}
      </span>
    </li>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <article className={`rounded-xl p-4 ${tone}`}>
      <p className="text-xs">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </article>
  );
}

function ReviewSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-24 w-96 animate-pulse rounded-xl bg-slate-200" />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-24 animate-pulse rounded-xl bg-slate-200"
          />
        ))}
      </div>
      <div className="h-72 animate-pulse rounded-2xl bg-slate-200" />
    </div>
  );
}
